// GreytHR Sync — Settings → Calendar → GreytHR Sync.
//
// Admin screen for the one-way GreytHR → em-Hub sync (backend
// services/greythr): run it, see what each run did, link em-Hub employees to
// GreytHR (matched by email, fixable by hand) and map GreytHR leave types to
// em-Hub ones. Gated by `manage_greythr_sync`.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  RefreshCw,
  Users,
  Plane,
  CalendarCheck,
  Scale,
  Link2,
  Unlink,
  Search,
  Plus,
  AlertTriangle,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import {
  Tabs,
  Chip,
  Select,
  Input,
  SearchInput,
  Pagination,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
} from "../../components/em/ui";
import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";

const PAGE_SIZE = 25;

const card = {
  background: "var(--em-bg-card)",
  border: `1px solid ${T.border}`,
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: T.shadowSm,
};
const cardHead = {
  padding: "16px 20px",
  borderBottom: `1px solid ${T.divider}`,
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
};
const th = {
  textAlign: "left",
  padding: "10px 16px",
  font: `600 11px ${T.font}`,
  color: T.fg5,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  background: T.bgHead,
  borderBottom: `1px solid ${T.divider}`,
  whiteSpace: "nowrap",
};
const td = {
  padding: "12px 16px",
  font: `400 13px ${T.font}`,
  color: T.fg1,
  borderBottom: `1px solid ${T.divider}`,
  verticalAlign: "middle",
};
const muted = { font: `400 12px ${T.font}`, color: T.fg5 };

const fmtTime = (s) => {
  if (!s) return "—";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime())
    ? String(s)
    : d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const MATCH_LABEL = {
  work_email: "Work email",
  personal_email: "Personal email",
  alternate_email: "Alternate email",
  lookup: "Email lookup",
  manual: "Manual",
};

const KIND_LABEL = {
  all: "Full sync",
  employees: "Employees",
  leave_types: "Leave types",
  transactions: "Leave",
  balances: "Balances",
};

// One line per step of a run, from the backend's summary object.
const summaryLines = (s) => {
  if (!s) return [];
  const out = [];
  if (s.employees) {
    const e = s.employees;
    out.push(
      `Employees: ${e.already_linked + e.newly_linked}/${e.members} linked` +
        (e.newly_linked ? ` (+${e.newly_linked} new)` : "") +
        (e.conflicts?.length ? `, ${e.conflicts.length} need a manual link` : "") +
        (e.unlinked_stale ? `, ${e.unlinked_stale} stale link(s) removed` : ""),
    );
  }
  if (s.leave_types) {
    const l = s.leave_types;
    out.push(
      `Leave types: ${l.categories} in GreytHR, ${l.unmapped} unmapped` +
        (l.auto_mapped ? `, ${l.auto_mapped} mapped by name` : ""),
    );
  }
  if (s.transactions) {
    const t = s.transactions;
    const unmapped = Object.values(t.skipped_unmapped_type || {}).reduce((a, b) => a + b, 0);
    const mode = t.mode === "backfill" ? " (back-fill for new mappings/links)" : t.mode === "full" ? " (full)" : "";
    out.push(
      `Leave ${t.range?.from} → ${t.range?.to}${mode}: ${t.fetched} in GreytHR · +${t.inserted} new, ${t.updated} updated, ` +
        `${t.linked_to_emhub_request} matched to em-Hub requests, ${t.pending_in_greythr || 0} pending in GreytHR, ` +
        `${(t.removed_in_greythr || 0) + (t.cancelled_in_greythr || 0)} removed/cancelled` +
        (t.skipped_employee_not_linked ? ` · ${t.skipped_employee_not_linked} skipped (employee not linked)` : "") +
        (t.skipped_member_without_login ? ` · ${t.skipped_member_without_login} skipped (no em-Hub login)` : "") +
        (unmapped ? ` · ${unmapped} skipped (leave type not mapped)` : ""),
    );
  }
  if (s.balances) out.push(`Balances ${s.balances.year}: ${s.balances.rows} rows for ${s.balances.employees} employees`);
  if (s.errors) Object.entries(s.errors).forEach(([k, v]) => out.push(`⚠ ${KIND_LABEL[k] || k}: ${v}`));
  return out;
};

const RUN_STATUS = {
  success: { label: "Success", bg: "#dcfce7", color: T.success600 },
  partial: { label: "Partial", bg: T.warn50, color: "#9a3412" },
  failed: { label: "Failed", bg: "#fee2e2", color: T.danger },
  running: { label: "Running", bg: "#dbeafe", color: T.info600 },
};
const RunStatus = ({ status }) => {
  const s = RUN_STATUS[status] || RUN_STATUS.running;
  return (
    <Chip bg={s.bg} color={s.color}>
      {s.label}
    </Chip>
  );
};

const StatTile = ({ icon: Icon, label, value, sub }) => (
  <div style={{ ...card, padding: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: T.orange50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={17} color={T.orange} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={muted}>{label}</div>
      <div style={{ font: `700 20px ${T.font}`, color: T.fg1, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ ...muted, marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

const GreytHRSync = () => {
  const api = `${API_URL}/api/greythr`;
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }),
    [],
  );
  const [view, setView] = useState("overview");
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/status`, auth);
      setStatus(res.data);
      setStatusError(null);
      return res.data;
    } catch (err) {
      setStatusError(err?.response?.data?.error || "Could not load the GreytHR sync status");
      return null;
    }
  }, [api, auth]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // While a run is going, poll until it finishes, then refresh whatever view
  // is open so the new links / mappings show.
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setInterval(async () => {
      const s = await loadStatus();
      if (s && !s.running) {
        setRefreshKey((k) => k + 1);
        const last = s.runs?.[0];
        if (last?.status === "success") toast.success(`${KIND_LABEL[last.kind] || "Sync"} finished`);
        else if (last) toast.warning(`${KIND_LABEL[last.kind] || "Sync"} finished with problems — see Recent runs`);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [status?.running, loadStatus]);

  const startSync = async (kind, full = false) => {
    if (full) {
      const ok = await confirmDialog({
        title: "Full resync",
        message: `Re-read every GreytHR leave from ${status?.ranges?.full?.from} to ${status?.ranges?.full?.to}. Leave removed in GreytHR is cancelled here. This can take a few minutes.`,
        confirmLabel: "Run full resync",
      });
      if (!ok) return;
    }
    try {
      await axios.post(`${api}/sync`, { kind, full }, auth);
      toast.success(`${KIND_LABEL[kind]} started`);
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not start the sync");
    }
  };

  const running = !!status?.running;

  return (
    <div>
      {statusError && (
        <div role="alert" style={{ ...card, padding: 14, marginBottom: 14, color: T.danger, font: `500 13px ${T.font}` }}>
          {statusError}
        </div>
      )}
      {status && !status.configured && (
        <div
          style={{
            ...card,
            padding: 14,
            marginBottom: 14,
            display: "flex",
            gap: 10,
            alignItems: "center",
            background: T.warn50,
          }}
        >
          <AlertTriangle size={16} color={T.warn} />
          <span style={{ font: `500 13px ${T.font}`, color: T.fg1 }}>
            GreytHR isn&apos;t configured on the server. Set GREYTHR_TENANT_DOMAIN, GREYTHR_USERNAME and
            GREYTHR_PASSWORD in the backend environment and restart it.
          </span>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Tabs
          tabs={[
            { key: "overview", label: "Overview", icon: RefreshCw },
            { key: "employees", label: "Employees", icon: Users },
            { key: "leavetypes", label: "Leave Types", icon: Plane },
          ]}
          active={view}
          onChange={setView}
        />
        <div style={{ flex: 1 }} />
        {running && (
          <Chip bg={T.info50} color={T.info600}>
            <RefreshCw size={12} /> {KIND_LABEL[status.running.kind]} running…
          </Chip>
        )}
        <PrimaryButton
          icon={RefreshCw}
          disabled={running || !status?.configured}
          onClick={() => startSync("all")}
          style={running || !status?.configured ? { opacity: 0.55, cursor: "default" } : undefined}
        >
          Sync now
        </PrimaryButton>
      </div>

      {view === "overview" && (
        <Overview status={status} running={running} onSync={startSync} />
      )}
      {view === "employees" && (
        <EmployeesView api={api} auth={auth} refreshKey={refreshKey} onChanged={loadStatus} running={running} onSync={startSync} />
      )}
      {view === "leavetypes" && (
        <LeaveTypesView api={api} auth={auth} refreshKey={refreshKey} onChanged={loadStatus} running={running} onSync={startSync} />
      )}
    </div>
  );
};

/* ───────────── Overview ───────────── */

const Overview = ({ status, running, onSync }) => {
  if (!status) return <div style={{ ...muted, padding: 20 }}>Loading…</div>;
  const c = status.counts || {};
  const disabled = running || !status.configured;
  const stepBtn = (kind, label, icon) => (
    <SecondaryButton
      icon={icon}
      disabled={disabled}
      onClick={() => onSync(kind)}
      style={disabled ? { opacity: 0.55, cursor: "default" } : undefined}
    >
      {label}
    </SecondaryButton>
  );
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <StatTile
          icon={Users}
          label="Employees linked"
          value={`${c.linked_members ?? 0} / ${c.members ?? 0}`}
          sub={`${c.greythr_active ?? 0} active in GreytHR (${c.greythr_employees ?? 0} total)`}
        />
        <StatTile
          icon={Plane}
          label="Leave types mapped"
          value={`${c.mapped_categories ?? 0} / ${c.categories ?? 0}`}
          sub="Unmapped GreytHR types aren't copied"
        />
        <StatTile
          icon={CalendarCheck}
          label="Leave copied from GreytHR"
          value={c.imported_leaves ?? 0}
          sub={`${c.linked_leaves ?? 0} em-Hub requests matched to GreytHR`}
        />
        <StatTile icon={Scale} label="GreytHR balance rows" value={c.balance_rows ?? 0} sub="Shown on Leave balance cards" />
      </div>

      <div style={card}>
        <div style={cardHead}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>Sync</div>
            <div style={muted}>
              {status.auto_sync
                ? `Runs automatically every ${status.interval_minutes} min`
                : "Automatic sync is off — run it from here"}
              {status.ranges?.incremental &&
                ` · copies leave from ${status.ranges.incremental.from} to ${status.ranges.incremental.to}`}
              {status.tenant && ` · ${status.tenant}`}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {stepBtn("employees", "Employees", Users)}
            {stepBtn("leave_types", "Leave types", Plane)}
            {stepBtn("transactions", "Leave", CalendarCheck)}
            {stepBtn("balances", "Balances", Scale)}
            <GhostButton
              disabled={disabled}
              onClick={() => onSync("all", true)}
              style={disabled ? { opacity: 0.55, cursor: "default" } : undefined}
            >
              Full resync
            </GhostButton>
          </div>
        </div>
        <div style={{ padding: "12px 20px", ...muted, lineHeight: 1.6 }}>
          One way: GreytHR is the source of truth and nothing is written back. Employees are matched by email (work,
          then personal, then em-Hub alternate emails). GreytHR leave shows here as Approved, and applications still
          awaiting approval in GreytHR as Pending; both can only be changed in GreytHR. Balance cards use
          GreytHR&apos;s balance for mapped leave types, minus em-Hub requests GreytHR doesn&apos;t have yet.
        </div>
      </div>

      <div style={card}>
        <div style={cardHead}>
          <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>Recent runs</div>
        </div>
        {status.runs?.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Started</th>
                  <th style={th}>Run</th>
                  <th style={th}>Status</th>
                  <th style={th}>By</th>
                  <th style={th}>What happened</th>
                </tr>
              </thead>
              <tbody>
                {status.runs.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtTime(r.started_at)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{KIND_LABEL[r.kind] || r.kind}</td>
                    <td style={td}>
                      <RunStatus status={r.status} />
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{r.triggered_by_name || "Automatic"}</td>
                    <td style={{ ...td, ...muted }}>
                      {summaryLines(r.summary).map((l) => (
                        <div key={l}>{l}</div>
                      ))}
                      {!r.summary && r.error && <div style={{ color: T.danger }}>{r.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "32px 20px", textAlign: "center", ...muted }}>No sync has run yet.</div>
        )}
      </div>
    </div>
  );
};

/* ───────────── Employees ───────────── */

const memberName = (m) => [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email || `#${m.id}`;

const EmployeesView = ({ api, auth, refreshKey, onChanged, running, onSync }) => {
  const [data, setData] = useState({ members: [], greythr_employees: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null); // member id
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/employees`, auth);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load employees");
    } finally {
      setLoading(false);
    }
  }, [api, auth]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => setPage(1), [filter, search]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.members.filter((m) => {
      if (filter === "linked" && !m.greythr_employee_id) return false;
      if (filter === "unlinked" && m.greythr_employee_id) return false;
      if (filter === "unlinked" && String(m.exit_status || "").toLowerCase() === "deleted") return false;
      if (!q) return true;
      return [memberName(m), m.email, m.greythr_name, m.greythr_email, m.greythr_employee_no]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data.members, filter, search]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visible = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const linkedCount = data.members.filter((m) => m.greythr_employee_id).length;
  // GreytHR employees free to link (not linked to anyone else).
  const available = data.greythr_employees.filter((g) => !g.member_id);

  const act = async (memberId, fn, okMsg) => {
    setBusy(memberId);
    try {
      const res = await fn();
      toast.success(res?.data?.message || okMsg);
      setEditing(null);
      await load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || "That didn't work");
    } finally {
      setBusy(null);
    }
  };
  const lookupLink = (m) => act(m.id, () => axios.post(`${api}/employees/${m.id}/lookup`, {}, auth), "Linked");
  const saveLink = (m) =>
    act(m.id, () => axios.put(`${api}/employees/${m.id}/link`, { greythr_employee_id: pick || null }, auth), "Saved");
  const unlink = async (m) => {
    const ok = await confirmDialog({
      title: "Unlink from GreytHR",
      message: `${memberName(m)} will stop receiving GreytHR leave and balances until linked again. Leave already copied stays.`,
      confirmLabel: "Unlink",
      danger: true,
    });
    if (ok) act(m.id, () => axios.put(`${api}/employees/${m.id}/link`, { greythr_employee_id: null }, auth), "Unlinked");
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <EmailLookup api={api} auth={auth} />

      <div style={card}>
        <div style={cardHead}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>em-Hub employees</div>
            <div style={muted}>
              {linkedCount} of {data.members.length} linked to GreytHR · {data.greythr_employees.length} GreytHR employees
              known
            </div>
          </div>
          <Tabs
            tabs={[
              { key: "all", label: "All" },
              { key: "linked", label: "Linked" },
              { key: "unlinked", label: "Not linked" },
            ]}
            active={filter}
            onChange={setFilter}
          />
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, employee no…"
            style={{ width: 260 }}
          />
          <SecondaryButton
            icon={RefreshCw}
            disabled={running}
            onClick={() => onSync("employees")}
            style={running ? { opacity: 0.55, cursor: "default" } : undefined}
          >
            Re-match by email
          </SecondaryButton>
        </div>
        {loading ? (
          <div style={{ padding: 20, ...muted }}>Loading…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Employee (em-Hub)</th>
                  <th style={th}>GreytHR employee</th>
                  <th style={th}>Matched by</th>
                  <th style={{ ...th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.id}>
                    <td style={td}>
                      <div style={{ font: `600 13px ${T.font}` }}>{memberName(m)}</div>
                      <div style={muted}>{m.email || "No email"}</div>
                      {!m.user_id && (
                        <div style={{ ...muted, color: T.warn }}>No em-Hub login — leave can&apos;t be copied</div>
                      )}
                    </td>
                    <td style={td}>
                      {editing === m.id ? (
                        <Select value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 260 }}>
                          <option value="">— Choose a GreytHR employee —</option>
                          {[
                            ...(m.greythr_employee_id
                              ? data.greythr_employees.filter((g) => Number(g.employee_id) === Number(m.greythr_employee_id))
                              : []),
                            ...available,
                          ].map((g) => (
                            <option key={g.employee_id} value={g.employee_id}>
                              {g.name} · {g.employee_no}
                              {g.email ? ` · ${g.email}` : ""}
                              {g.left_org ? " (left)" : ""}
                            </option>
                          ))}
                        </Select>
                      ) : m.greythr_employee_id ? (
                        <>
                          <div style={{ font: `600 13px ${T.font}` }}>
                            {m.greythr_name || `GreytHR #${m.greythr_employee_id}`}{" "}
                            <span style={{ ...muted, fontWeight: 500 }}>{m.greythr_employee_no}</span>
                          </div>
                          <div style={muted}>
                            {m.greythr_email || "—"}
                            {m.greythr_left_org ? " · left the organisation" : ""}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: T.fg7 }}>Not linked</span>
                      )}
                    </td>
                    <td style={td}>
                      {m.greythr_match ? (
                        <Chip>{MATCH_LABEL[m.greythr_match] || m.greythr_match}</Chip>
                      ) : (
                        <span style={{ color: T.fg7 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {editing === m.id ? (
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <SecondaryButton onClick={() => setEditing(null)}>Cancel</SecondaryButton>
                          <PrimaryButton disabled={busy === m.id || !pick} onClick={() => saveLink(m)}>
                            Save
                          </PrimaryButton>
                        </div>
                      ) : (
                        <div style={{ display: "inline-flex", gap: 4 }}>
                          {!m.greythr_employee_id && m.email && (
                            <GhostButton icon={Search} disabled={busy === m.id} onClick={() => lookupLink(m)}>
                              Find by email
                            </GhostButton>
                          )}
                          <GhostButton
                            icon={Link2}
                            disabled={busy === m.id}
                            onClick={() => {
                              setEditing(m.id);
                              setPick(m.greythr_employee_id ? String(m.greythr_employee_id) : "");
                            }}
                          >
                            {m.greythr_employee_id ? "Change" : "Link"}
                          </GhostButton>
                          {m.greythr_employee_id && (
                            <GhostButton
                              icon={Unlink}
                              disabled={busy === m.id}
                              onClick={() => unlink(m)}
                              style={{ color: T.danger }}
                            >
                              Unlink
                            </GhostButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!visible.length && (
                  <tr>
                    <td colSpan={4} style={{ ...td, textAlign: "center", color: T.fg7, padding: 32 }}>
                      No employees match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Pagination page={page} totalPages={totalPages} total={rows.length} pageSize={PAGE_SIZE} onPage={setPage} />
    </div>
  );
};

// Live GreytHR lookup — "who is this email in GreytHR?"
const EmailLookup = ({ api, auth }) => {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await axios.get(`${api}/lookup`, { ...auth, params: { email: email.trim() } });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const e = result?.employee;
  return (
    <div style={card}>
      <form onSubmit={run} style={{ ...cardHead, borderBottom: result || error ? cardHead.borderBottom : 0 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>Look up by email</div>
          <div style={muted}>Asks GreytHR directly which employee an email belongs to</div>
        </div>
        <Input
          type="email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder="name@company.com"
          style={{ width: 280 }}
        />
        <SecondaryButton type="submit" icon={Search} disabled={loading}>
          {loading ? "Looking…" : "Look up"}
        </SecondaryButton>
      </form>
      {error && <div style={{ padding: "12px 20px", color: T.danger, font: `500 13px ${T.font}` }}>{error}</div>}
      {e && (
        <div style={{ padding: "12px 20px", display: "flex", gap: 24, flexWrap: "wrap", font: `400 13px ${T.font}` }}>
          <div>
            <div style={muted}>Name</div>
            <b>{e.name}</b>
          </div>
          <div>
            <div style={muted}>Employee no</div>
            {e.employeeNo}
          </div>
          <div>
            <div style={muted}>GreytHR id</div>
            {e.employeeId}
          </div>
          <div>
            <div style={muted}>Joined</div>
            {e.dateOfJoin ? String(e.dateOfJoin).slice(0, 10) : "—"}
          </div>
          <div>
            <div style={muted}>Status</div>
            {e.leftOrg ? "Left the organisation" : "Active"}
          </div>
          <div>
            <div style={muted}>em-Hub</div>
            {result.linked_member ? `Linked to ${result.linked_member.name}` : "Not linked to anyone"}
          </div>
        </div>
      )}
    </div>
  );
};

/* ───────────── Leave types ───────────── */

const LeaveTypesView = ({ api, auth, refreshKey, onChanged, running, onSync }) => {
  const [data, setData] = useState({ categories: [], leave_types: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/leave-types`, auth);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not load leave types");
    } finally {
      setLoading(false);
    }
  }, [api, auth]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const act = async (categoryId, fn) => {
    setBusy(categoryId);
    try {
      const res = await fn();
      toast.success(res?.data?.message || "Saved");
      await load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || "That didn't work");
    } finally {
      setBusy(null);
    }
  };
  // The full set of em-Hub types for a GreytHR type.
  const setMapping = (c, ids) =>
    act(c.category_id, () => axios.put(`${api}/leave-types/${c.category_id}`, { leave_type_ids: ids }, auth));
  const mappedIds = (c) => (c.leave_types || []).map((t) => Number(t.id));
  const addType = (c, id) => id && setMapping(c, [...mappedIds(c), Number(id)]);
  const removeType = (c, id) => setMapping(c, mappedIds(c).filter((x) => x !== Number(id)));
  const create = (c) => act(c.category_id, () => axios.post(`${api}/leave-types/${c.category_id}/create`, {}, auth));

  // "Contractor / Consultant" etc. for a type's applies_to_types, to tell
  // same-named em-Hub types apart.
  const [empTypes, setEmpTypes] = useState(new Map());
  useEffect(() => {
    axios
      .get(`${API_URL}/api/settings/employment-types`, auth)
      .then((r) => setEmpTypes(new Map((r.data || []).map((t) => [Number(t.id), t.name]))))
      .catch(() => {});
  }, [auth]);
  const appliesTo = (t) => {
    try {
      const ids = t.applies_to_types ? JSON.parse(t.applies_to_types) : null;
      if (!Array.isArray(ids) || !ids.length) return "all employee types";
      const names = ids.map((id) => empTypes.get(Number(id))).filter(Boolean);
      return names.length ? names.join(", ") : "some employee types";
    } catch {
      return "all employee types";
    }
  };

  const unmapped = data.categories.filter((c) => !(c.leave_types || []).length).length;

  return (
    <div style={card}>
      <div style={cardHead}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>GreytHR leave types</div>
          <div style={muted}>
            {data.categories.length} found in GreytHR · {unmapped} not mapped. Leave of an unmapped type isn&apos;t
            copied. Map a GreytHR type to several em-Hub types when em-Hub splits it by employee type — each
            employee&apos;s leave goes to the one they&apos;re eligible for. After a new mapping, the next leave sync
            copies that type from the start of the year.
          </div>
        </div>
        <SecondaryButton
          icon={RefreshCw}
          disabled={running}
          onClick={() => onSync("leave_types")}
          style={running ? { opacity: 0.55, cursor: "default" } : undefined}
        >
          Refresh from GreytHR
        </SecondaryButton>
        <SecondaryButton
          icon={CalendarCheck}
          disabled={running}
          onClick={() => onSync("transactions")}
          style={running ? { opacity: 0.55, cursor: "default" } : undefined}
        >
          Sync leave
        </SecondaryButton>
      </div>
      {loading ? (
        <div style={{ padding: 20, ...muted }}>Loading…</div>
      ) : data.categories.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>GreytHR leave type</th>
                <th style={th}>Employees with a balance</th>
                <th style={th}>em-Hub leave type</th>
                <th style={th}>Copied leaves</th>
                <th style={{ ...th, textAlign: "right" }} />
              </tr>
            </thead>
            <tbody>
              {data.categories.map((c) => {
                const mapped = c.leave_types || [];
                // Types not mapped to any GreytHR type yet.
                const options = data.leave_types.filter((t) => !t.greythr_category_id);
                return (
                  <tr key={c.category_id}>
                    <td style={td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Chip>{c.code || c.category_id}</Chip>
                        <span style={{ font: `600 13px ${T.font}` }}>{c.description}</span>
                      </div>
                    </td>
                    <td style={td}>{c.employees_with_balance ?? 0}</td>
                    <td style={td}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                        {mapped.map((t) => (
                          <Chip
                            key={t.id}
                            bg={T.bgMuted}
                            color={T.fg1}
                            border={`1px solid ${T.border}`}
                            style={{ padding: "3px 4px 3px 10px" }}
                          >
                            <span title={`For ${appliesTo(t)}`}>
                              {t.name}
                              <span style={{ color: T.fg5, fontWeight: 500 }}> · {appliesTo(t)}</span>
                            </span>
                            <button
                              type="button"
                              title={`Unmap ${t.name}`}
                              disabled={busy === c.category_id}
                              onClick={() => removeType(c, t.id)}
                              style={{
                                border: 0,
                                background: "transparent",
                                cursor: "pointer",
                                color: T.fg5,
                                padding: "0 4px",
                                font: `600 13px/1 ${T.font}`,
                              }}
                            >
                              ×
                            </button>
                          </Chip>
                        ))}
                        {!mapped.length && <span style={{ color: T.fg7 }}>Not mapped</span>}
                        {options.length > 0 && (
                          <Select
                            value=""
                            disabled={busy === c.category_id}
                            onChange={(e) => addType(c, e.target.value)}
                            style={{ width: "auto", minWidth: 170, padding: "5px 30px 5px 10px" }}
                          >
                            <option value="">{mapped.length ? "+ Add another" : "+ Map to…"}</option>
                            {options.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} · {appliesTo(t)}
                                {Number(t.is_paid) === 0 ? " (unpaid)" : ""}
                              </option>
                            ))}
                          </Select>
                        )}
                      </div>
                    </td>
                    <td style={td}>{mapped.length ? c.imported_leaves ?? 0 : "—"}</td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {!mapped.length && (
                        <GhostButton icon={Plus} disabled={busy === c.category_id} onClick={() => create(c)}>
                          Create in em-Hub
                        </GhostButton>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: "32px 20px", textAlign: "center", ...muted }}>
          No GreytHR leave types yet — run &quot;Refresh from GreytHR&quot;.
        </div>
      )}
    </div>
  );
};

export default GreytHRSync;
